/**
 * PreviewWelcomeHero — the cinematic welcome card from the program preview
 * design (Figma node 11126:816151). Renders dark-overlay imagery with a
 * bottom-aligned title block and a glass call-to-action pill.
 *
 * Designed dark-by-default (the imagery + bottom gradient does the work) and
 * adapts naturally to the user's theme via the translucent gradient frame.
 *
 * Reusable: the trainer preview passes "Welcome to {program.name}" + "Start
 * the program"; the learner runtime can pass any title/CTA pair.
 */

import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { EASE_OUT } from '@/lib/motion';

export interface PreviewWelcomeHeroProps {
  /** Signed-URL cover image. Falls back to a brand gradient when absent. */
  coverUrl?: string | null;
  title: string;
  description?: string | null;
  /** Glass pill placed opposite the title (e.g. "2 days"). */
  durationLabel?: string;
  /** Bottom glass CTA. Hidden when omitted. */
  cta?: {
    label: string;
    onClick?: () => void;
    /** Leading icon. */
    icon?: React.ReactNode;
  };
  /** Orange-outline back pill rendered above the cinematic card. */
  backAction?: {
    label: string;
    onClick: () => void;
  };
  /**
   * Shorter cover proportion used on the learner course detail so the
   * comments section header is visible above the fold without scrolling.
   */
  compact?: boolean;
}

function ClockBadgeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 1a6 6 0 110 12A6 6 0 018 2zm0 2.5a.5.5 0 01.5.5v2.793l1.52 1.52a.5.5 0 11-.708.707l-1.666-1.667A.5.5 0 017.5 8V5a.5.5 0 01.5-.5z"
        fill="currentColor"
      />
    </svg>
  );
}

function PowerIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M18.36 6.64a9 9 0 11-12.73 0M12 2v10" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

interface BackPillProps {
  label: string;
  onClick: () => void;
}

/** Orange-outline back pill matching the Figma "Go back to course library"
 *  spec: 1 px secondary-500 stroke, fully rounded, arrow-left icon + bold label. */
export function BackPill({ label, onClick }: BackPillProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-full border border-secondary-500 text-secondary-500 text-[13px] font-bold leading-none active:scale-[0.97] hover:bg-secondary-500/[0.06] transition-[background-color,transform] duration-150"
    >
      <BackArrowIcon />
      {label}
    </button>
  );
}

function BackArrowIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.358 5.392a.625.625 0 010 .883L5.258 9.375h6.825c.794 0 1.972.233 2.969.95 1.029.742 1.823 1.972 1.823 3.842a.625.625 0 11-1.25 0c0-1.464-.596-2.317-1.302-2.826-.74-.533-1.645-.716-2.24-.716H5.258l3.1 3.1a.625.625 0 11-.884.883l-4.166-4.166a.625.625 0 010-.883L7.475 5.392a.625.625 0 01.883 0z"
        fill="currentColor"
      />
    </svg>
  );
}

interface DurationBadgeProps {
  label: string;
}

/** Mirrors the duration badge on /programs ProgramCard:
 *  double-layered white pill (mix-blend-overlay + 60% white) with dark text. */
function DurationBadge({ label }: DurationBadgeProps) {
  return (
    <span className="relative inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded-[6px] overflow-hidden shrink-0">
      <span aria-hidden="true" className="absolute inset-0 bg-white mix-blend-overlay rounded-[6px]" />
      <span aria-hidden="true" className="absolute inset-0 bg-white/60 rounded-[6px]" />
      <ClockBadgeIcon className="relative w-4 h-4 text-grey-900" />
      <span className="relative text-xs font-semibold text-grey-900 whitespace-nowrap">
        {label}
      </span>
    </span>
  );
}

export default function PreviewWelcomeHero({
  coverUrl,
  title,
  description,
  durationLabel,
  cta,
  backAction,
  compact = false,
}: PreviewWelcomeHeroProps) {
  const reduceMotion = useReducedMotion();

  return (
    <motion.section
      initial={reduceMotion ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: EASE_OUT }}
      // Outer gradient frame matches the wizard step-card chrome
      className="relative rounded-[28px] p-[10px] sm:p-3"
      style={{
        background: 'linear-gradient(to right, rgb(var(--color-primary) / 0.22), rgb(var(--color-secondary) / 0.22))',
      }}
    >
      <div
        className={`relative overflow-hidden rounded-[20px] ${
          compact
            ? 'aspect-[16/6] sm:aspect-[24/6] min-h-[200px]'
            : 'aspect-[16/10] sm:aspect-[21/10] min-h-[280px]'
        }`}
        style={{ boxShadow: '0 8px 28px rgba(0,0,0,0.18)' }}
      >
        {/* Cover or fallback gradient */}
        {coverUrl ? (
          <img src={coverUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div
            className="absolute inset-0"
            style={{
              background:
                `linear-gradient(135deg, ${'rgb(var(--color-primary-900))'} 0%, ${'rgb(var(--color-primary-950))'} 50%, ${'rgb(var(--color-primary-900))'} 100%)`,
            }}
            aria-hidden="true"
          />
        )}

        {/* Dark gradient — bottom-up — improves text legibility */}
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.10) 35%, rgba(0,0,0,0.55) 75%, rgba(0,0,0,0.78) 100%)',
          }}
        />

        {/* Bottom content stack */}
        <div
          className={`absolute inset-x-0 bottom-0 flex flex-col ${
            compact ? 'p-4 sm:p-5 gap-3 sm:gap-4' : 'p-5 sm:p-7 gap-4 sm:gap-5'
          }`}
        >
          <div className="w-full">
            <div className="flex items-start justify-between gap-3">
              <h1 className="text-2xl sm:text-3xl font-extrabold text-white leading-tight tracking-tight flex-1 min-w-0">
                {title}
              </h1>
              {durationLabel && <DurationBadge label={durationLabel} />}
            </div>
            {description && (
              <p className="text-sm text-white/85 leading-relaxed mt-2 line-clamp-3">
                {description}
              </p>
            )}
          </div>

          {/* Glass CTA — full-width pill, white/24, 46px tall */}
          {cta && (
            <button
              type="button"
              onClick={cta.onClick}
              className="flex flex-row justify-center items-center gap-2 self-stretch w-full h-[46px] px-2 py-1 rounded-full bg-white/[0.24] hover:bg-white/30 active:scale-[0.98] transition-[background-color,transform] duration-150 text-white text-[17px] leading-[22px] font-bold"
              style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
            >
              {cta.icon ?? <PowerIcon />}
              {cta.label}
            </button>
          )}
        </div>
      </div>

      {backAction && (
        <div className="flex justify-center pt-3 sm:pt-4">
          <BackPill label={backAction.label} onClick={backAction.onClick} />
        </div>
      )}
    </motion.section>
  );
}
